/**
 * לקוח ה-API להתראת "מחיר בסיס עלה" — TASKS 5.4, PRD 7.3.
 */
import type { AdminApiError } from './adminApi.js';

export type PriceAlertStatus = 'open' | 'resolved' | 'dismissed';

export interface PriceChangeAlert {
  id: number;
  product_id: number;
  variant_id: number | null;
  old_base_price: string;
  new_base_price: string;
  affected_customers_count: number;
  status: PriceAlertStatus;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  product_name: string;
  product_sku: string;
  variant_label: string | null;
  variant_sku: string | null;
}

async function parseErrorBody(res: Response): Promise<AdminApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    return {
      status: res.status,
      code: body.error?.code ?? 'UNKNOWN_ERROR',
      message: body.error?.message ?? 'אירעה שגיאה לא צפויה.',
    };
  } catch {
    return { status: res.status, code: 'UNKNOWN_ERROR', message: 'אירעה שגיאה לא צפויה.' };
  }
}

export async function listPriceAlerts(
  status: PriceAlertStatus | 'all' = 'open',
): Promise<{ alerts: PriceChangeAlert[] }> {
  const res = await fetch(`/api/admin/price-alerts?status=${status}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { alerts: PriceChangeAlert[] };
}

export interface AffectedCustomer {
  customer_id: number;
  full_name: string;
  phone_e164: string;
  fixed_price: string;
}

/** ⭐ נספר בזמן אמת בשרת — ⛔ לא ה-`affected_customers_count` שנשמר ברגע הפתיחה. */
export async function getPriceAlertAffectedCustomers(
  id: number,
): Promise<{ customers: AffectedCustomer[] }> {
  const res = await fetch(`/api/admin/price-alerts/${id}/customers`, {
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { customers: AffectedCustomer[] };
}

export type ResolvePriceAlertAction = 'update_all' | 'update_selected' | 'dismiss';

export async function resolvePriceAlert(
  id: number,
  action: ResolvePriceAlertAction,
  customerIds?: number[],
): Promise<{ alert: PriceChangeAlert; updatedCustomerIds: number[] }> {
  const res = await fetch(`/api/admin/price-alerts/${id}/resolve`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, customerIds }),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { alert: PriceChangeAlert; updatedCustomerIds: number[] };
}
